// 🔧 CORREGIR EmailLayout.tsx - Agregar folderPath a EmailRow

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '../components/base/Button';
import PhoenixOffcanvas from '../components/base/PhoenixOffcanvas';
import SearchBox from '../components/common/SearchBox';
import EmailSidebar from '../components/modules/email/EmailSidebar';
import { useBreakpoints } from '../providers/BreakpointsProvider';
import { useMainLayoutContext } from '../providers/MainLayoutProvider';
import React, { PropsWithChildren, useEffect, useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import InboxToolbar from '../components/modules/email/InboxToolbar';
import { Email } from '../data/email';
import EmailRow from '../components/modules/email/EmailRow';
import { Link, useLocation, useParams } from 'react-router-dom';
import { faBars, faPlus } from '@fortawesome/free-solid-svg-icons';
import BulkSelectProvider from '../providers/BulkSelectProvider';

interface EmailLayoutProps {
  page: 'inbox' | 'detail' | 'compose';
  emails?: Email[];
  onEmailSelect?: (emailId: number, folderPath: string) => void;
}

const EmailLayout = ({
  children,
  page,
  emails = [],
  onEmailSelect
}: PropsWithChildren<EmailLayoutProps>) => {
  const [openSidebar, setOpenSidebar] = useState(false);
  const { breakpoints } = useBreakpoints();
  const { setContentClass } = useMainLayoutContext();
  const location = useLocation();
  const { folderPath } = useParams<{ folderPath?: string }>();
  
  // 🔧 FUNCIÓN PARA DETERMINAR LA CARPETA ACTUAL
  const getCurrentFolder = (): string => {
    if (folderPath) {
      return decodeURIComponent(folderPath);
    }
    
    if (location.pathname.includes('/inbox')) {
      return 'INBOX';
    }
    
    if (location.pathname.includes('/sent')) {
      return 'Sent';
    }
    
    if (location.pathname.includes('/drafts')) {
      return 'Drafts';
    }
    
    if (location.pathname.includes('/trash')) {
      return 'Trash';
    }
    
    if (location.pathname.includes('/spam')) {
      return 'Spam';
    }
    
    return 'INBOX';
  };
  
  const currentFolder = getCurrentFolder();

  useEffect(() => {
    setContentClass('pt-0');

    return () => {
      setContentClass('');
    };
  }, []);

  // Determinar si mostrar vista de 3 columnas
  const showThreeColumnView = page === 'inbox' && location.pathname.includes('email-detail');

  return (
    <><div className="email-container">
      <Row className="gx-lg-4 gx-xl-6 gx-3 py-4 z-2 position-sticky bg-body email-header">
        <Col className="col-auto">
          <Link
            className="btn btn-primary email-sidebar-width d-none d-lg-block"
            to="/anubis/email/compose"
          >
            Compose
          </Link>
          <Button
            variant="phoenix-secondary"
            className="px-3 text-body-tertiary d-lg-none"
            onClick={() => setOpenSidebar(true)}
          >
            <FontAwesomeIcon icon={faBars} />
          </Button>
        </Col>
        {page !== 'compose' && (
          <Col className="col-auto d-lg-none">
            <Button variant="primary" className="px-3 px-sm-4" as="a" href="/anubis/email/compose">
              <span className="d-none d-sm-inline-block">Compose</span>
              <FontAwesomeIcon icon={faPlus} className="d-sm-none" />
            </Button>
          </Col>
        )}
        <Col className="col-auto flex-1">
          <SearchBox className="w-100" />
        </Col>
      </Row>
      <Row className="g-lg-4 g-xl-6 mb-8">
        {/* Sidebar - Siempre visible */}
        <Col lg="auto">
          <div
            className="email-sidebar email-sidebar-width bg-body d-none d-lg-block"
            id="emailSidebarColumn"
          >
            <EmailSidebar />
          </div>
          {breakpoints.down('lg') && (
            <PhoenixOffcanvas
              open={openSidebar}
              fixed
              placement="start"
              className="email-sidebar email-sidebar-width bg-body"
              backdropClassName="top-0"
              onHide={() => setOpenSidebar(false)}
            >
              <EmailSidebar hideSidebar={() => setOpenSidebar(false)} />
            </PhoenixOffcanvas>
          )}
        </Col>

        {/* Vista de 3 columnas cuando estamos en inbox + detail */}
        {showThreeColumnView ? (
          <>
            {/* Lista de emails - columna central */}
            <Col xs={12} md={4} lg={3} xl={3}>
              <div className="email-content scrollbar">
                <div className="px-lg-1">
                  <BulkSelectProvider data={emails}>
                    <InboxToolbar size="sm" />
                    {emails.map((email, index) => (
                      <div
                        key={email.id}
                        onClick={() => onEmailSelect && onEmailSelect(email.id, currentFolder)}
                        style={{ cursor: 'pointer' }}
                      >
                        <EmailRow 
                          email={email} 
                          index={index} 
                          folderPath={currentFolder} // ← AGREGAR ESTA LÍNEA
                        />
                      </div>
                    ))}
                  </BulkSelectProvider>
                </div>
              </div>
            </Col>

            {/* Detalle del email - columna derecha */}
            <Col xs={12} md={8} lg="auto" className="flex-1">
              {children}
            </Col>
          </>
        ) : (
          <>
            {/* Vista normal de 2 columnas */}
            {page !== 'inbox' && (
              <Col xs="3" className="d-none d-xxl-block">
                <div className="email-content scrollbar">
                  <div className="px-lg-1">
                    <BulkSelectProvider data={emails}>
                      <InboxToolbar size="sm" />
                      {emails.map((email, index) => (
                        <EmailRow 
                          email={email} 
                          index={index} 
                          folderPath={currentFolder} // ← AGREGAR ESTA LÍNEA
                          key={email.id} 
                        />
                      ))}
                    </BulkSelectProvider>
                  </div>
                </div>
              </Col>
            )}
            {children}
          </>
        )}
      </Row>
    </div></>
  );
};

export default EmailLayout;